import React from 'react';

import dayjs from 'dayjs';
import styled from 'styled-components';

import BorderedStyledTable from 'common/Table/BorderedStyledTable';
import HighlightedRowOverlay from 'common/Table/HighlightedRowOverlay';
import { CmiDigestWatchlistIncentive } from 'types/cmiDigestWatchlist';

const TableContainer = styled.div`
  position: relative;
  z-index: 0;
`;

const HighlightedRowsTable = styled(BorderedStyledTable)`
  &&& {
    .ant-table-tbody > tr.highlighted-row > td {
      border-color: transparent !important;
      background: transparent !important;
    }
  }
`;

const IncentiveCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IncentiveText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IncentiveTitle = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const IncentiveMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--competitor-purple);
  color: var(--white);
  font-size: 10px;
  font-family: var(--font-bold);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const TextCell = styled.div`
  font-size: 14px;
  color: var(--text-primary);
`;

const AddedCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AddedDate = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const AddedTime = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const columns = [
  {
    title: 'Incentive',
    width: '30%',
    key: 'incentive',
    render: (_value: unknown, record: CmiDigestWatchlistIncentive & { isNewest?: boolean }) => (
      <IncentiveCell>
        <img src="/icons/price-tag-green.svg" alt="" width={16} height={16} />
        <IncentiveText>
          <IncentiveTitle>{record.description || '-'}</IncentiveTitle>
          {record.isNewest && (
            <IncentiveMetaRow>
              <NewBadge>NEW</NewBadge>
            </IncentiveMetaRow>
          )}
        </IncentiveText>
      </IncentiveCell>
    ),
  },
  {
    title: 'Competitor',
    width: '22%',
    key: 'competitor',
    render: (_value: unknown, record: CmiDigestWatchlistIncentive) => <TextCell>{record.competitor_name}</TextCell>,
  },
  {
    title: 'Care Type',
    width: '20%',
    key: 'care-type',
    render: (_value: unknown, record: CmiDigestWatchlistIncentive) => (
      <TextCell>{record.care_types.join(', ') || '-'}</TextCell>
    ),
  },
  {
    title: 'Date',
    width: '14%',
    key: 'date',
    render: (_value: unknown, record: CmiDigestWatchlistIncentive) => (
      <TextCell>{record.date ? dayjs(record.date).format('MMM D YYYY') : '-'}</TextCell>
    ),
  },
  {
    title: 'Added',
    width: '14%',
    key: 'added',
    render: (_value: unknown, record: CmiDigestWatchlistIncentive) => (
      <AddedCell>
        <AddedDate>{dayjs(record.created_at).format('MMM D YYYY')}</AddedDate>
        <AddedTime>{dayjs(record.created_at).format('h:mm A')}</AddedTime>
      </AddedCell>
    ),
  },
];

type Props = {
  rows: CmiDigestWatchlistIncentive[];
};

const CommunitiesToWatchIncentivesTable = ({ rows }: Props) => {
  const dataSource = rows.map((row, index) => ({ ...row, isNewest: index === 0 }));
  const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);

  return (
    <TableContainer ref={setContainerEl}>
      <HighlightedRowsTable
        columns={columns as any}
        dataSource={dataSource}
        rowKey={((record: CmiDigestWatchlistIncentive) => record.id) as any}
        rowClassName={(record: any) => (record.isNewest ? 'highlighted-row' : '')}
        pagination={false}
        showSorterTooltip={false}
      />
      <HighlightedRowOverlay containerEl={containerEl} rowSelector="tr.highlighted-row" deps={[dataSource]} />
    </TableContainer>
  );
};

export default CommunitiesToWatchIncentivesTable;
